import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  ForkInspectionTemplateMutation,
  ForkInspectionTemplateMutationVariables,
  SourceType,
} from 'graphql-query-contracts';
import { forkInspectionTemplateMutation } from 'graphql-queries';
import { ForkInspectionTemplateError } from './errors/ForkInspectionTemplateError';

export type ForkInspectionTemplateInput = {
  parentTemplateId: string;
  buildingId: {
    entityId: string;
    entityType: string;
    sourceType: SourceType;
  };
};

export type ForkInspectionTemplateResult = {
  templateId: string;
};

export type UseForkInspectionTemplateResult = {
  forkInspectionTemplate: (
    input: ForkInspectionTemplateInput
  ) => Promise<ForkInspectionTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateForkInput(input: ForkInspectionTemplateInput): void {
  if (!input.parentTemplateId?.trim()) {
    throw new ForkInspectionTemplateError('parentTemplateId is required');
  }

  if (!input.buildingId?.entityId?.trim()) {
    throw new ForkInspectionTemplateError('buildingId.entityId is required');
  }

  if (!input.buildingId?.entityType?.trim()) {
    throw new ForkInspectionTemplateError('buildingId.entityType is required');
  }
}

export const useForkInspectionTemplate =
  (): UseForkInspectionTemplateResult => {
    const [forkTemplate, { loading }] = useMutation<
      ForkInspectionTemplateMutation,
      ForkInspectionTemplateMutationVariables
    >(forkInspectionTemplateMutation);

    const [error, setError] = useState<Error | undefined>(undefined);

    const forkInspectionTemplate = useCallback(
      async (
        input: ForkInspectionTemplateInput
      ): Promise<ForkInspectionTemplateResult> => {
        try {
          setError(undefined);

          validateForkInput(input);

          const variables: ForkInspectionTemplateMutationVariables = {
            input: {
              parentTemplateId: input.parentTemplateId,
              buildingId: {
                sourceEntityId: input.buildingId.entityId,
                sourceEntityType: input.buildingId.entityType,
                sourceType: input.buildingId.sourceType,
              },
            },
          };

          const mutationResult = await forkTemplate({ variables });

          if (!mutationResult.data?.forkInspectionTemplate?.templateId) {
            throw new ForkInspectionTemplateError();
          }

          return {
            templateId: mutationResult.data.forkInspectionTemplate.templateId,
          };
        } catch (err) {
          const wrappedError =
            err instanceof ForkInspectionTemplateError
              ? err
              : new ForkInspectionTemplateError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to fork inspection template'
                );

          setError(wrappedError);

          throw wrappedError;
        }
      },
      [forkTemplate]
    );

    return {
      forkInspectionTemplate,
      loading,
      error,
    };
  };
